import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Search, ShieldOff, Unlock, Users } from "lucide-react";
import { atualizarRevendaDistribuidor, listarRevendasDistribuidor } from "../services/api";

const ListaRevendasDistribuidor = ({ token }) => {
  const navigate = useNavigate();
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);

  const carregar = async () => {
    setLoading(true);
    try {
      const res = await listarRevendasDistribuidor(token);
      setItens(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setItens([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return itens;
    return itens.filter((r) =>
      [r.razaoSocial, r.nomeFantasia, r.cnpj, r.cidade].some((v) =>
        String(v || "").toLowerCase().includes(termo)
      )
    );
  }, [itens, busca]);

  const alternarAtivo = async (revenda) => {
    const bloquear = revenda.ativo !== false;
    if (!window.confirm(bloquear ? `Bloquear ${revenda.nomeFantasia || revenda.razaoSocial}?` : "Desbloquear esta revenda?")) return;
    try {
      await atualizarRevendaDistribuidor(revenda.id, { ...revenda, ativo: !bloquear }, token);
      await carregar();
    } catch (e) {
      alert(e?.response?.data || "Erro ao alterar o status da revenda.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Revendas</h2>
            <p className="text-sm text-gray-500 dark:text-slate-300/80">Revendas vinculadas a este distribuidor.</p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/revendas/novo")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm"
          >
            <Plus size={16} /> Nova revenda
          </button>
        </div>

        <div className="relative mb-4">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por nome, CNPJ ou cidade"
            className="w-full pl-9 pr-3 py-2 rounded-xl border dark:bg-slate-950/40 dark:border-white/10"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="text-left p-3">Revenda</th>
                <th className="text-left p-3">CNPJ</th>
                <th className="text-left p-3">Cidade</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500">Carregando...</td></tr>
              )}
              {!loading && filtradas.map((r) => (
                <tr key={r.id} className="border-t dark:border-white/10">
                  <td className="p-3">
                    <div className="font-medium">{r.nomeFantasia || r.razaoSocial}</div>
                    <div className="text-xs text-gray-500">{r.razaoSocial}</div>
                  </td>
                  <td className="p-3">{r.cnpj}</td>
                  <td className="p-3">{[r.cidade, r.estado].filter(Boolean).join(" / ") || "—"}</td>
                  <td className="p-3">{r.ativo === false ? "Bloqueada" : "Ativa"}</td>
                  <td className="p-3 text-right space-x-2">
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => navigate(`/revendas/usuarios?revendaId=${r.id}`)}>
                      <Users size={14} /> Usuários
                    </button>
                    <button type="button" className="inline-flex items-center gap-1 text-blue-600" onClick={() => navigate(`/revendas/editar/${r.id}`)}>
                      <Edit size={14} /> Editar
                    </button>
                    <button type="button" className="inline-flex items-center gap-1" onClick={() => alternarAtivo(r)}>
                      {r.ativo === false ? <Unlock size={14} /> : <ShieldOff size={14} />}
                      {r.ativo === false ? "Desbloquear" : "Bloquear"}
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && !filtradas.length && (
                <tr><td colSpan={5} className="p-6 text-center text-gray-500">Nenhuma revenda encontrada.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ListaRevendasDistribuidor;
